/* WorkspaceSection — iter101.56 (refined)
 *
 * Shared "workspace surface" primitive used across planner tab
 * bodies (Submission / MFG). Adopts the Author-the-plan header
 * aesthetic per user direction — the user liked THAT look and
 * wants Submission and MFG to visually match:
 *
 *   ┌────────────────────────────────────────────────────────┐
 *   │  SECTION TITLE                                          │
 *   │  Italic subhead sits below the caps title              │
 *   ├────────────────────────────────────────────────────────┤
 *   │  Body content                                           │
 *   └────────────────────────────────────────────────────────┘
 *
 * Design tokens (all matching the PlanEditorInline header):
 *   · Container: rounded-2xl border-border/70 + soft depth shadow.
 *   · Header strip: `bg-secondary/30` gray tint, border-b.
 *   · Title: text-[11px] tracking-[0.16em] uppercase text-foreground.
 *   · Subhead: text-[12px] italic text-muted-foreground.
 *   · Right slot: reserved for buttons (Expand all, Save indicator,
 *     status pills, Unlock controls, …).
 *
 * Kept the old `tone` prop for optional per-surface tone-coding on
 * a hair-thin top accent bar — used sparingly so it doesn't fight
 * the calm gray header strip. Default is no accent bar.
 */
import React from "react";


const TONE_BAR = {
    primary: "bg-teal-500",
    violet:  "bg-violet-500",
    emerald: "bg-emerald-500",
    amber:   "bg-amber-500",
    rose:    "bg-rose-500",
    slate:   "bg-slate-400",
};


export default function WorkspaceSection({
    title,
    subhead,
    tone,
    right = null,
    padding = "p-4 sm:p-5",
    testid,
    children,
}) {
    const barCls = tone ? (TONE_BAR[tone] || TONE_BAR.primary) : null;
    const hasHeader = Boolean(title);
    return (
        <div
            data-testid={testid}
            className="relative rounded-2xl border border-border/70 bg-card overflow-hidden shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]"
        >
            {/* Optional tone-coded 2px top accent bar — off by default
                so the Author-the-plan calm aesthetic is respected. */}
            {barCls && (
                <span aria-hidden className={`absolute top-0 left-0 right-0 h-[2px] ${barCls} opacity-90 z-10`} />
            )}

            {hasHeader && (
                <div className="bg-secondary/30 border-b border-border/70 px-5 sm:px-6 py-3.5">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <h2 className="text-[11px] font-bold tracking-[0.16em] uppercase text-foreground">
                                {title}
                            </h2>
                            {subhead && (
                                <p className="text-[12px] italic text-muted-foreground mt-0.5 max-w-xl">
                                    {subhead}
                                </p>
                            )}
                        </div>
                        {right && <div className="flex items-center gap-2 flex-wrap shrink-0">{right}</div>}
                    </div>
                </div>
            )}
            <div className={padding}>{children}</div>
        </div>
    );
}
